import {
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'

import { getSignUrl, uploadFile } from 'api/upload'
import { Button } from 'components/Button'
import { FileView } from 'components/FileView'
import { Icon } from 'components/Icon'
import { Input } from 'components/Input'
import { TagSelector } from 'components/TagSelector'
import { UploadBox } from 'components/UploadBox'
import {
  ACCEPT_IMAGES,
  MAX_LINE_IMAGE_SIZE,
  ONE_MB,
  sendAllMessageStrings,
} from '../../constants'
import type { TTag } from 'types'
import type { TSurveyQuestionType } from 'api/surveys'
import generateFileName from 'utils/generateFileName'
import { validateFile } from 'utils/file'
import {
  createEmptyChoice,
  createEmptyQuestion,
  type TSurveyBuilderQuestion,
} from './builder'
import {
  AspectRatioToggle,
  checkboxClassName,
  RadioPillGroup,
  SectionCard,
  selectClassName,
} from './fields'

const MIN_CHOICES = 2
const MAX_CHOICES = 8
const MAX_QUESTIONS = 10

const QUESTION_TYPE_OPTIONS: ReadonlyArray<{
  value: TSurveyQuestionType
  label: string
}> = [
  { value: 'SINGLE', label: '単一選択' },
  { value: 'MULTI', label: '複数選択' },
  { value: 'TEXT', label: '自由記述' },
  { value: 'DATE', label: '日付' },
]

type Props = {
  questions: TSurveyBuilderQuestion[]
  setQuestions: Dispatch<SetStateAction<TSurveyBuilderQuestion[]>>
  tags: TTag[]
  showTags?: boolean
  onRemoveComboReferences: (
    questionClientId: string,
    choiceClientId?: string,
  ) => void
  onUploadingChange: (uploading: boolean) => void
}

const isChoiceType = (type: TSurveyQuestionType) =>
  type === 'SINGLE' || type === 'MULTI'

export function QuestionEditor({
  questions,
  setQuestions,
  tags,
  showTags = true,
  onRemoveComboReferences,
  onUploadingChange,
}: Props) {
  const [imageError, setImageError] = useState<string | null>(null)
  const [uploadingClientIds, setUploadingClientIds] = useState<
    ReadonlySet<string>
  >(new Set())

  useEffect(() => {
    onUploadingChange(uploadingClientIds.size > 0)
  }, [uploadingClientIds, onUploadingChange])

  const updateQuestion = (
    questionClientId: string,
    update: (question: TSurveyBuilderQuestion) => TSurveyBuilderQuestion,
  ) => {
    setQuestions((current) =>
      current.map((question) =>
        question.clientId === questionClientId ? update(question) : question,
      ),
    )
  }

  const moveQuestion = (questionClientId: string, direction: -1 | 1) => {
    setQuestions((current) => {
      const questionIndex = current.findIndex(
        (question) => question.clientId === questionClientId,
      )
      const destinationIndex = questionIndex + direction
      if (
        questionIndex < 0 ||
        destinationIndex < 0 ||
        destinationIndex >= current.length
      ) {
        return current
      }
      const next = [...current]
      const [movedQuestion] = next.splice(questionIndex, 1)
      next.splice(destinationIndex, 0, movedQuestion)
      return next
    })
  }

  const removeQuestion = (questionClientId: string) => {
    if (questions.length <= 1) return
    setQuestions((current) =>
      current.filter((question) => question.clientId !== questionClientId),
    )
    onRemoveComboReferences(questionClientId)
  }

  const toggleChoiceTag = (
    questionClientId: string,
    choiceClientId: string,
    tagId: number,
  ) => {
    updateQuestion(questionClientId, (current) => ({
      ...current,
      choices: (current.choices ?? []).map((currentChoice) => {
        if (currentChoice.clientId !== choiceClientId) return currentChoice
        const tagIds = currentChoice.tagIds ?? []
        return {
          ...currentChoice,
          tagIds: tagIds.includes(tagId)
            ? tagIds.filter((id) => id !== tagId)
            : [...tagIds, tagId],
        }
      }),
    }))
  }

  const handleSelectQuestionImage = async (
    questionClientId: string,
    file: File,
  ) => {
    if (uploadingClientIds.has(questionClientId)) return
    setImageError(null)
    const isValid = validateFile({
      file,
      acceptedTypes: ACCEPT_IMAGES,
      maxBytes: MAX_LINE_IMAGE_SIZE,
      toastError: (error) => setImageError(error.message),
    })
    if (!isValid) return

    setUploadingClientIds((current) => new Set(current).add(questionClientId))
    try {
      const signed = await getSignUrl({
        filename: generateFileName(file),
        contentType: file.type,
        size: file.size,
      })
      await uploadFile(signed.preSignedUrl, file)
      updateQuestion(questionClientId, (current) => ({
        ...current,
        imageUrl: signed.uploadedUrl,
        imageAspectRatio: current.imageAspectRatio ?? '16:9',
      }))
    } catch {
      setImageError('画像のアップロードに失敗しました。')
    } finally {
      setUploadingClientIds((current) => {
        const next = new Set(current)
        next.delete(questionClientId)
        return next
      })
    }
  }

  return (
    <SectionCard
      title="設問"
      description="友だちに表示される質問です。上下ボタンで並べ替えできます。"
      headingId="survey-questions-heading"
      badge={
        <span className="rounded-full bg-surface px-3 py-1 text-xs font-semibold text-muted">
          {questions.length} / {MAX_QUESTIONS}問
        </span>
      }
    >
      {imageError ? (
        <p className="text-xs font-normal text-danger">{imageError}</p>
      ) : null}
      {questions.map((question, questionIndex) => (
        <div
          key={question.clientId}
          className="space-y-4 rounded-2xl border border-border/60 bg-white p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent text-xs font-bold text-white">
                {questionIndex + 1}
              </span>
              <h3 className="text-sm font-semibold text-ink">
                設問 {questionIndex + 1}
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={`設問 ${questionIndex + 1}を上へ移動`}
                disabled={questionIndex === 0}
                onClick={() => moveQuestion(question.clientId, -1)}
              >
                <Icon icon="chevronUp" width={14} height={14} />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={`設問 ${questionIndex + 1}を下へ移動`}
                disabled={questionIndex === questions.length - 1}
                onClick={() => moveQuestion(question.clientId, 1)}
              >
                <Icon icon="chevronDown" width={14} height={14} />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-danger"
                aria-label={`設問 ${questionIndex + 1}を削除`}
                disabled={questions.length <= 1}
                onClick={() => removeQuestion(question.clientId)}
              >
                削除
              </Button>
            </div>
          </div>

          <label className="grid gap-2 text-xs font-semibold text-ink">
            <span>設問文</span>
            <Input
              maxLength={500}
              aria-label={`設問 ${questionIndex + 1}の設問文`}
              placeholder="例：当店をどこで知りましたか？"
              value={question.title}
              onChange={(event) =>
                updateQuestion(question.clientId, (current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
              required
            />
          </label>

          <div className="grid gap-2 text-xs font-semibold text-ink">
            <span>設問画像（任意）</span>
            {question.imageUrl ? (
              <div className="grid gap-3">
                <div className="flex h-36 overflow-hidden rounded-xl bg-surface/60 p-2">
                  <FileView
                    className={`h-full max-h-32 rounded-lg object-cover ${
                      question.imageAspectRatio === '1:1'
                        ? 'aspect-square'
                        : 'aspect-video'
                    }`}
                    file={{ url: question.imageUrl, type: 'image/png' }}
                    onRemove={() =>
                      updateQuestion(question.clientId, (current) => ({
                        ...current,
                        imageUrl: undefined,
                        imageAspectRatio: undefined,
                      }))
                    }
                  />
                </div>
                <div className="grid gap-1.5">
                  <span>画像の比率</span>
                  <AspectRatioToggle
                    name={`question-aspect-${question.clientId}`}
                    ariaLabel={`設問 ${questionIndex + 1}の画像比率`}
                    value={question.imageAspectRatio === '1:1' ? '1:1' : '16:9'}
                    onChange={(imageAspectRatio) =>
                      updateQuestion(question.clientId, (current) => ({
                        ...current,
                        imageAspectRatio,
                      }))
                    }
                  />
                </div>
              </div>
            ) : (
              <UploadBox
                title={
                  uploadingClientIds.has(question.clientId)
                    ? 'アップロード中...'
                    : sendAllMessageStrings.uploadPhoto
                }
                icon="image"
                acceptTypes={ACCEPT_IMAGES}
                maxSizeMb={MAX_LINE_IMAGE_SIZE / ONE_MB}
                onSelect={(file) => {
                  void handleSelectQuestionImage(question.clientId, file)
                }}
              />
            )}
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="grid gap-2 text-xs font-semibold text-ink">
              <span>回答タイプ</span>
              <RadioPillGroup
                name={`question-type-${question.clientId}`}
                ariaLabel={`設問 ${questionIndex + 1}のタイプ`}
                options={QUESTION_TYPE_OPTIONS}
                value={question.type}
                onChange={(nextType) => {
                  const wasChoiceType = isChoiceType(question.type)
                  const willBeChoiceType = isChoiceType(nextType)

                  updateQuestion(question.clientId, (current) => {
                    if (wasChoiceType && willBeChoiceType) {
                      return {
                        ...current,
                        type: nextType,
                        maxSelections:
                          nextType === 'MULTI'
                            ? current.maxSelections
                            : undefined,
                      }
                    }

                    const empty = createEmptyQuestion(nextType)
                    return {
                      ...empty,
                      id: current.id,
                      clientId: current.clientId,
                      title: current.title,
                      required: current.required,
                      imageUrl: current.imageUrl,
                      imageAspectRatio: current.imageAspectRatio,
                    }
                  })

                  if (wasChoiceType && !willBeChoiceType) {
                    onRemoveComboReferences(question.clientId)
                  }
                }}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className={checkboxClassName}
                checked={question.required ?? true}
                onChange={(event) =>
                  updateQuestion(question.clientId, (current) => ({
                    ...current,
                    required: event.target.checked,
                  }))
                }
              />
              <span>回答を必須にする</span>
            </label>
          </div>

          {question.type === 'MULTI' ? (
            <label className="grid max-w-[240px] gap-2 text-xs font-semibold text-ink">
              <span>最大選択数</span>
              <select
                className={selectClassName}
                aria-label={`設問 ${questionIndex + 1}の最大選択数`}
                value={question.maxSelections ?? ''}
                onChange={(event) => {
                  const rawValue = event.target.value
                  updateQuestion(question.clientId, (current) => ({
                    ...current,
                    maxSelections: rawValue ? Number(rawValue) : undefined,
                  }))
                }}
              >
                <option value="">制限なし</option>
                {Array.from(
                  { length: question.choices?.length ?? MAX_CHOICES },
                  (_, index) => index + 1,
                ).map((count) => (
                  <option key={count} value={count}>
                    {count}個まで
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {isChoiceType(question.type) ? (
            <div className="grid gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-ink">選択肢</span>
                <span className="text-xs text-muted">
                  {question.choices?.length ?? 0} / {MAX_CHOICES}個
                </span>
              </div>
              {(question.choices ?? []).map((choice, choiceIndex) => (
                <div
                  key={choice.clientId}
                  className="grid gap-3 rounded-xl border border-border/60 bg-surface/60 p-4"
                >
                  <div className="flex items-end gap-2">
                    <label className="grid flex-1 gap-2 text-xs font-semibold text-ink">
                      <span>選択肢 {choiceIndex + 1}</span>
                      <Input
                        maxLength={255}
                        aria-label={`設問 ${questionIndex + 1} 選択肢 ${choiceIndex + 1}`}
                        value={choice.label}
                        onChange={(event) =>
                          updateQuestion(question.clientId, (current) => ({
                            ...current,
                            choices: (current.choices ?? []).map(
                              (currentChoice) =>
                                currentChoice.clientId === choice.clientId
                                  ? {
                                      ...currentChoice,
                                      label: event.target.value,
                                    }
                                  : currentChoice,
                            ),
                          }))
                        }
                        required
                      />
                    </label>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="shrink-0 text-danger"
                      aria-label={`設問 ${questionIndex + 1} 選択肢 ${choiceIndex + 1}を削除`}
                      disabled={(question.choices?.length ?? 0) <= MIN_CHOICES}
                      onClick={() => {
                        updateQuestion(question.clientId, (current) => {
                          const choices = (current.choices ?? []).filter(
                            (currentChoice) =>
                              currentChoice.clientId !== choice.clientId,
                          )
                          return {
                            ...current,
                            choices,
                            maxSelections:
                              current.maxSelections === undefined
                                ? undefined
                                : Math.min(
                                    current.maxSelections,
                                    choices.length,
                                  ),
                          }
                        })
                        onRemoveComboReferences(
                          question.clientId,
                          choice.clientId,
                        )
                      }}
                    >
                      <Icon icon="close" color="currentColor" width={16} height={16} />
                    </Button>
                  </div>

                  {showTags && (
                    <div className="grid gap-1.5">
                      <span className="text-xs font-semibold text-ink">
                        選択時に付けるタグ
                      </span>
                      <p className="text-xs text-muted">
                        この選択肢を選んで回答した友だちに自動で付与されます。クリックで選択・解除できます。
                      </p>
                      {tags.length === 0 ? (
                        <p className="text-xs text-muted">
                          利用できるタグがありません。タグ管理から作成できます。
                        </p>
                      ) : (
                        <TagSelector
                          tags={tags}
                          selectedTagIds={choice.tagIds ?? []}
                          onToggle={(tagId) =>
                            toggleChoiceTag(
                              question.clientId,
                              choice.clientId,
                              tagId,
                            )
                          }
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}

              {(question.choices?.length ?? 0) < MAX_CHOICES ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="justify-self-start"
                  onClick={() =>
                    updateQuestion(question.clientId, (current) => ({
                      ...current,
                      choices: [
                        ...(current.choices ?? []),
                        createEmptyChoice((current.choices?.length ?? 0) + 1),
                      ],
                    }))
                  }
                >
                  <Icon icon="plus" color="currentColor" width={14} height={14} className="mr-1" />
                  選択肢を追加
                </Button>
              ) : null}
            </div>
          ) : null}

          {question.type === 'TEXT' ? (
            <p className="rounded-xl bg-surface/80 px-4 py-3 text-xs text-muted">
              友だちは自由にテキストを入力して回答します。
            </p>
          ) : null}
          {question.type === 'DATE' ? (
            <p className="rounded-xl bg-surface/80 px-4 py-3 text-xs text-muted">
              友だちはカレンダーから日付を選んで回答します。
            </p>
          ) : null}
        </div>
      ))}

      {questions.length < MAX_QUESTIONS ? (
        <Button
          type="button"
          variant="secondary"
          className="justify-self-start"
          onClick={() =>
            setQuestions((current) => [...current, createEmptyQuestion()])
          }
        >
          <Icon icon="plus" color="currentColor" width={14} height={14} className="mr-1" />
          設問を追加
        </Button>
      ) : null}
    </SectionCard>
  )
}
